import React, { useMemo, useState } from 'react';
import type { Contract } from '../../../types/contract.type';
import type { ContractMilestone } from '../../../types/contractMilestone.type';
import ContractMilestoneEditorModal, { type MilestoneDraft } from './modals/MilestoneEditorModal';
import { toProjectDateOnly } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

function toNum(v: any): number {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function fmtKRW(v?: number | null) {
    return typeof v === 'number'
        ? new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW', maximumFractionDigits: 0 }).format(v)
        : '—';
}

function companyLabel(contract: Contract) {
    return (
        (contract as any)?.projectCompany?.company?.name ??
        (contract as any)?.projectCompany?.name ??
        (contract as any)?.partyName ??
        '(회사 미지정)'
    );
}

function criteriaPreview(raw: any): { lines: { text: string; done?: boolean }[]; needsClamp: boolean } {
    // completionCriteria가:
    // - [{id,text,done}] 배열일 수도 있고
    // - string[] 일 수도 있고
    // - 기타(JSON)일 수도 있으니 최대한 안전하게 처리
    const arr = Array.isArray(raw) ? raw : [];
    const lines = arr
        .map((x) => {
            if (typeof x === 'string') return { text: x };
            if (x && typeof x === 'object') return { text: String(x.text ?? ''), done: !!x.done };
            return { text: '' };
        })
        .filter((x) => x.text.trim().length > 0);

    const needsClamp = lines.length > 4;
    return { lines, needsClamp };
}

export default function MilestonesList({
    contract,
    items,
    onCreate,
    onUpdate,
    onDelete,
    canEdit,
}: {
    contract: Contract;
    items: ContractMilestone[];
    onCreate: (draft: MilestoneDraft) => void;
    onUpdate: (id: string, patch: MilestoneDraft) => void;
    onDelete: (id: string) => void;
    canEdit?: boolean;
}) {
    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    const [q, setQ] = useState('');
    const [modal, setModal] = useState<{ open: boolean; value: ContractMilestone | null; isNew: boolean }>({
        open: false,
        value: null,
        isNew: false,
    });

    const rows = useMemo(() => {
        const s = q.trim().toLowerCase();
        const base = items.slice().sort((a, b) => a.seq - b.seq);

        if (!s) return base;

        return base.filter((m) => {
            const crit = Array.isArray((m as any).completionCriteria) ? (m as any).completionCriteria : [];
            const hay = [
                m.label ?? '',
                m.note ?? '',
                ...crit.map((c: any) => (typeof c === 'string' ? c : c?.text ?? '')),
            ]
                .join(' ')
                .toLowerCase();
            return hay.includes(s);
        });
    }, [items, q]);

    const openNew = () => {
        const nextSeq = rows.length ? Math.max(...rows.map((r) => r.seq)) + 1 : 1;
        setModal({
            open: true,
            value: {
                id: '',
                contractId: contract.id,
                seq: nextSeq,
                label: '',
                percent: null,
                amountPlan: null,
                dueDate: null,
                status: 'PLANNED',
                // ✅ ActualPayment 분리 이후에도 모달이 필드를 가지고 있을 수 있으므로 값은 유지(서버 전송은 API에서 pick으로 차단)
                actualAmount: null,
                eventDate: null,
                note: null,
                linkedWbsIds: [],
                completionCriteria: [],
                createdAt: null,
                updatedAt: null,
            } as any,
            isNew: true,
        });
    };

    const openEdit = (m: ContractMilestone) => setModal({ open: true, value: m, isNew: false });

    return (
        <div className="w-full mt-6 space-y-4">
            {/* 상단 검색/버튼 영역 (MilestoneListForm UX) */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <input
                        className="w-64 rounded-lg border px-2 py-1 //text-sm"
                        placeholder="Search Milestone…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                    />
                    <span className="text-xs text-gray-500">Total: {rows.length}</span>
                </div>

                <button
                    className="text-xs rounded-lg border px-2.5 py-1.5 bg-white hover:bg-gray-50 disabled:opacity-50"
                    onClick={openNew}
                    disabled={!canEdit}
                    title={!canEdit ? '권한이 없습니다.' : undefined}
                >
                    + New Milestone
                </button>
            </div>

            <section className="rounded-2xl border bg-white overflow-hidden">
                <div className="px-4 py-3 border-b text-sm font-medium flex items-center justify-between">
                    <div>
                        {companyLabel(contract)} · {contract.contractName}
                        <span className="text-xs text-gray-500 ml-2">
                            총액 {fmtKRW(toNum((contract as any).contractAmount))}
                        </span>
                    </div>
                    <div className="text-xs text-gray-500">총 {rows.length}건</div>
                </div>

                <div className="overflow-x-auto">
                    {/* MilestoneListForm 느낌의 넓은 테이블 */}
                    <table className="min-w-[1100px] w-full //text-sm">
                        <thead>
                            <tr className="text-left text-gray-500">
                                <th className="px-3 py-2 w-16">No.</th>

                                <th className="px-3 py-2 w-[360px]">
                                    <div>Milestone Activity</div>
                                    <div className="text-xs text-gray-500 font-medium">내역</div>
                                </th>

                                <th className="px-3 py-2 w-16">%</th>

                                <th className="px-3 py-2 w-36">
                                    <div>Amount</div>
                                    <div className="text-xs text-gray-500 font-medium">금액(계획)</div>
                                </th>

                                <th className="px-3 py-2 w-24">
                                    <div>Due Date</div>
                                    <div className="text-xs text-gray-500 font-medium">계획일</div>
                                </th>

                                <th className="px-3 py-2 w-24">
                                    <div>Actual Date</div>
                                    <div className="text-xs text-gray-500 font-medium">완료일</div>
                                </th>

                                <th className="px-3 py-2 w-[360px]">
                                    <div>Completion Criteria</div>
                                    <div className="text-xs text-gray-500 font-medium">완료 판정 기준</div>
                                </th>

                                <th className="px-3 py-2 w-36">
                                    <div>Invoice</div>
                                    <div className="text-xs text-gray-500 font-medium">인보이스</div>
                                </th>

                                <th className="px-3 py-2 w-[320px]">
                                    <div>Status Note</div>
                                    <div className="text-xs text-gray-500 font-medium">진행 상황</div>
                                </th>

                                <th className="px-3 py-2 w-[150px]">
                                    <div>Links</div>
                                    <div className="text-xs text-gray-500 font-medium">연결</div>
                                </th>

                                {canEdit && <th className="px-3 py-2 w-24" />}
                            </tr>
                        </thead>

                        <tbody>
                            {rows.map((m, idx) => {
                                const plan =
                                    (m as any).amountPlan ??
                                    ((m as any).percent != null
                                        ? toNum((contract as any).contractAmount) * (toNum((m as any).percent) / 100)
                                        : null);

                                const dueDate = (m as any).dueDate ?? '—';

                                // ✅ ActualPayment 분리: 여기서는 완료일을 표시할 근거가 없으므로 ‘—’
                                const actualDate = '—';

                                const { lines, needsClamp } = criteriaPreview((m as any).completionCriteria);
                                const linkedCount = Array.isArray((m as any).linkedWbsIds)
                                    ? (m as any).linkedWbsIds.length
                                    : 0;

                                return (
                                    <tr
                                        key={m.id}
                                        className="border-t hover:bg-gray-50"
                                        title="클릭하여 상세 보기/수정"
                                    >
                                        <td className="px-3 py-2 text-gray-500">{idx + 1}</td>

                                        <td className="px-3 py-2 cursor-pointer" onClick={() => openEdit(m)}>
                                            <div className="font-medium">{m.label || '(제목 없음)'}</div>
                                            <div className="text-[11px] text-gray-500">
                                                Seq {m.seq} · Status {String((m as any).status ?? '—')}
                                            </div>
                                        </td>

                                        <td className="px-3 py-2 text-gray-500">{(m as any).percent ?? '-'}</td>

                                        <td className="px-3 py-2">{fmtKRW(plan)}</td>

                                        {/* <td className="px-3 py-2">{dueDate?.slice(0, 10) || '-'}</td> */}
                                        <td className="px-3 py-2">{toProjectDateOnly(dueDate, zone) || '-'}</td>

                                        <td className="px-3 py-2">{toProjectDateOnly(actualDate, zone) || '-'}</td>

                                        <td className="px-3 py-2 cursor-pointer" onClick={() => openEdit(m)}>
                                            <div className="relative">
                                                <ul className="list-disc pl-4 space-y-1 line-clamp-4 overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical]">
                                                    {lines.map((c, i) => (
                                                        <li
                                                            key={i}
                                                            className={c.done ? 'line-through text-gray-500' : ''}
                                                        >
                                                            {c.text}
                                                        </li>
                                                    ))}
                                                    {lines.length === 0 && <li className="text-gray-400">—</li>}
                                                </ul>

                                                {needsClamp && (
                                                    <>
                                                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-white to-transparent" />
                                                        <span className="pointer-events-none absolute right-1 bottom-0.5 text-xs text-gray-400 select-none">
                                                            …
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </td>

                                        {/* Invoice: 아직 모델/기능 없으니 placeholder */}
                                        <td className="px-3 py-2 text-gray-400">—</td>

                                        <td className="px-3 py-2 cursor-pointer" onClick={() => openEdit(m)}>
                                            <div className="whitespace-pre-wrap line-clamp-3">
                                                {(m as any).note ?? '—'}
                                            </div>
                                        </td>

                                        {/* Links: linkedWbsIds 정도만 표시(UX 유지용) */}
                                        <td className="px-3 py-2 text-xs text-gray-600">WP Links: {linkedCount}</td>

                                        {canEdit && (
                                            <td className="px-3 py-2 text-right">
                                                <button
                                                    className="text-xs rounded border px-2 py-1 hover:bg-gray-50"
                                                    onClick={() => {
                                                        // eslint-disable-next-line no-restricted-globals
                                                        if (!confirm('이 마일스톤을 삭제할까요?')) return;
                                                        onDelete(m.id);
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}

                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={canEdit ? 11 : 10} className="px-3 py-6 text-center text-gray-500">
                                        항목이 없습니다.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <ContractMilestoneEditorModal
                open={modal.open}
                onClose={() => setModal({ open: false, value: null, isNew: false })}
                value={modal.value as any}
                canEdit={canEdit}
                onSave={(draft) => {
                    if (!modal.value) return;
                    if (modal.isNew) onCreate(draft);
                    else onUpdate(modal.value.id, draft);
                    setModal({ open: false, value: null, isNew: false });
                }}
                onDelete={(id) => onDelete(id)}
            />
        </div>
    );
}
